

import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const API_BASE = "https://api.mahal.qa/api";

const MahalHomeSections = () => {
  const sectionRefs = useRef({});
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);

  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const localizeNumber = (value) => {
  return new Intl.NumberFormat(isArabic ? "ar-QA" : "en-US").format(
    Number(value || 0)
  );
};

const localizeCurrency = (value) => {
  return isArabic
    ? `${localizeNumber(value)} ${t("qar")}`
    : `${t("qar")} ${localizeNumber(value)}`;
};

const localizeWeight = (value) => {
  if (!value) return isArabic ? t("unit") : "1 unit";

  if (!isArabic) return value;

  const unit = String(value).trim().toLowerCase();

  const unitMap = {
    kg: t("kg"),
    kgram: t("kg"),
    kilogram: t("kg"),

    g: t("gram"),
    gram: t("gram"),

    box: t("box"),
    piece: t("piece"),
    pcs: t("pcs"),
    unit: t("unit"),
    pack: t("pack"),
    tray: t("tray"),
    litre: t("litre"),
    liter: t("litre"),
    l: t("litre"),
  };

  return unitMap[unit] || value;
};

  /* ================= FETCH PRODUCTS ================= */

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const lang = i18n.language?.startsWith("ar") ? "ar" : "en";
        const res = await fetch(`${API_BASE}/gridlist?lang=${lang}`);
        const json = await res.json();

        const products = json.products || [];

        const grouped = {};

        products.forEach((item) => {
        const category = item.category_display_name || item.category || "Others";

          if (!grouped[category]) {
            grouped[category] = [];
          }

          grouped[category].push({
            id: item.id, // ✅ FIX
            name: isArabic
              ? item.name_ar || item.product_name_arabic || item.name
              : item.name_en || item.product_name_english || item.name,
            weight: localizeWeight(item.unit_of_measure || "1 unit"),
            price: item.price_numeric || 0,
            oldPrice: null,
            off: null,
            img: item.img1,
            stock: item.stock || 1, // ✅ FIX
          });
        });

        setGroupedData(grouped);
        setLoading(false);
      } catch (err) {
        console.error("Error loading products:", err);
        setLoading(false);
      }
    };

    loadProducts();
  }, [i18n.language]);

  /* ================= ADD TO CART ================= */

const addToCart = (product) => {

  const token = localStorage.getItem("token");

  if (!token) {
   alert(t("please_login"));
    return;
  }

  axios
    .post(
      `${API_BASE}/cart/add`,
      {
        product_id: Number(product.id),
        quantity: 1,
        price: product.price,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )

    .then((res) => {

      // ✅ Product already exists
      if (res.data.status === "exists") {

  alert(t("product_already_cart"));

      }

      // ✅ New product added
      else {

        alert(t("added_to_cart"));

      }

      console.log("Added to cart");

    })

    .catch((err) => {

      console.error(
        "ADD TO CART ERROR",
        err
      );

      alert(err.response?.data?.error || t("backend_error"));

    });

};
  /* ================= SCROLL ================= */

  const scrollToSection = (category) => {
    sectionRefs.current[category]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (loading) {
    return <p style={{ textAlign: "center" }}>{t("loading_products")}</p>;
  }

  return (
    <>
      {Object.entries(groupedData).map(([category, products]) => (
        <ProductSection
          key={category}
          category={category}
          products={products}
          addToCart={addToCart}
          isArabic={isArabic}
          t={t}
          localizeCurrency={localizeCurrency}
          ref={(el) => (sectionRefs.current[category] = el)}
        />
      ))}
    </>
  );
};

/* ================= PRODUCT SECTION ================= */

const ProductSection = React.forwardRef(
  ({ category, products, addToCart, isArabic, t , localizeCurrency}, ref) => {
    const scrollRef = useRef(null);

    const scroll = (dir) => {
      if (!scrollRef.current) return;

      const amount = 300;

      if (dir === "left") {
        scrollRef.current.scrollLeft += isArabic ? amount : -amount;
      } else {
        scrollRef.current.scrollLeft += isArabic ? -amount : amount;
      }
    };

    return (
     <section
  className={`delivery_strip ${isArabic ? "arabic_delivery" : ""}`}
  dir={isArabic ? "rtl" : "ltr"}
>
        <div className="container">

          {/* HEADER */}
          <div className="mahal-section-header">
            <h2>{category}</h2>

            <div className="mahal-nav-arrows">
              <button onClick={() => scroll("left")}>‹</button>
              <button onClick={() => scroll("right")}>›</button>
            </div>
          </div>

          {/* PRODUCTS */}
          <div className="mahal-product-row" ref={scrollRef}>
            {products.map((item, index) => (
              <div className="mahal-product-card" key={index}>

                {/* IMAGE */}
                <img
                  src={item.img || "/fallback.png"}
                  alt="product"
                  className="mahal-dummy-img"
                />

                {/* TITLE */}
                <h5>{item.name}</h5>
                <p className="mahal-weight">{item.weight}</p>

                {/* PRICE */}
                <div className="mahal-price-row">
                  <div>
                    <span className="mahal-price">{localizeCurrency(item.price)}</span>
                    {item.oldPrice && (
                     <span className="mahal-old">{localizeCurrency(item.oldPrice)}</span>
                    )}
                  </div>

                  {/* BUTTON */}
                  <button
                    type="button"
                    className="mahal-add-btn"
                    disabled={!item.stock || item.stock === 0} // ✅ FIX
                    onClick={() => addToCart(item)} // ✅ FIX
                  >
                   {t("add")}
                    <span></span>
                  </button>

                </div>

              </div>
            ))}
          </div>

        </div>
      </section>
    );
  }
);

export default MahalHomeSections;